import type { CSSProperties } from 'react'
import { TOTAL_HEIGHT } from './bottomNavNotch'
import ShareIcon from './icons/ShareIcon'
import type { InstallPromptController } from '../app/useInstallPrompt'
import './InstallPromptSheet.css'

/**
 * The "add Respect Football to your home screen" nudge, docked directly above the
 * bottom nav.
 *
 * One element across both states — iOS's instructions expand this sheet in place rather than
 * swapping it for another, which is why the step body and the actions branch inside the same
 * `<aside>` instead of the caller choosing between two components.
 *
 * A nudge, not a blocker: no scrim, and the page behind it stays interactive (`role="status"`, not
 * `dialog` — nothing here traps focus or demands an answer).
 */
export default function InstallPromptSheet({
  controller,
}: {
  controller: InstallPromptController
}) {
  const { sheet, showInstructions, install, dismiss } = controller
  if (!sheet) return null

  const isInstructions = sheet.step === 'instructions'

  return (
    <aside
      className="install-prompt"
      role="status"
      aria-label="Install Respect Football"
      // The dock offset comes from the same constants the bar's own height is built from
      // (bottomNavNotch.ts), so the two can't drift; the safe-area inset is added in CSS because
      // only CSS can read it.
      style={{ '--install-prompt-dock': `${TOTAL_HEIGHT}px` } as CSSProperties}
    >
      <p className="install-prompt__headline">
        {isInstructions ? "Two taps and you're in" : 'Get the full Respect Football experience'}
      </p>

      {/* Both bodies stay mounted so the swap can animate the sheet's height (see the slot rules in
          InstallPromptSheet.css); the hidden one is taken out of the accessibility tree rather than
          out of the DOM. */}
      <div
        className={`install-prompt__slot ${isInstructions ? 'install-prompt__slot--collapsed' : ''}`}
        aria-hidden={isInstructions}
      >
        <div className="install-prompt__slot-inner">
          <p className="install-prompt__body">
            Full screen, no browser bar, one tap from your home screen.
          </p>
        </div>
      </div>

      <div
        className={`install-prompt__slot ${isInstructions ? '' : 'install-prompt__slot--collapsed'}`}
        aria-hidden={!isInstructions}
      >
        <div className="install-prompt__slot-inner">
          <ol className="install-prompt__steps">
            <li className="install-prompt__step">
              <span className="install-prompt__step-number">1</span>
              <span className="install-prompt__step-text">
                Tap <ShareIcon className="install-prompt__share-icon" /> at the bottom of Safari
              </span>
            </li>
            <li className="install-prompt__step">
              <span className="install-prompt__step-number">2</span>
              <span className="install-prompt__step-text">
                {/* "Scroll down" is the instruction, not filler: Safari buries the entry partway
                    down the share sheet, and that is where people give up. */}
                Scroll down, choose{' '}
                <span className="install-prompt__chip">Add to Home Screen</span>
              </span>
            </li>
          </ol>
        </div>
      </div>

      {/* "Not now" first in the DOM as well as on the left, so the reading order matches what is on
          screen. */}
      <div className="install-prompt__actions">
        <button type="button" className="install-prompt__secondary" onClick={dismiss}>
          Not now
        </button>
        {/* State 2 has no primary button — there is nothing left for the app to do. */}
        {!isInstructions && (
          <button
            type="button"
            className="install-prompt__primary"
            onClick={sheet.platform === 'android' ? install : showInstructions}
          >
            {sheet.platform === 'android' ? 'Install' : 'Show me how'}
          </button>
        )}
      </div>
    </aside>
  )
}
